import type { ThemeConfig } from "antd";
import { colors, font, radius, shadow, size, space } from "./tokens";

const px = (value: string) => parseInt(value, 10);

/**
 * The design tokens, expressed as antd v5 design tokens.
 *
 * antd v5 derives hundreds of values from ~20 seeds at runtime, so this object
 * replaces the 174-line `.ant-*` override sheet that v4 needed. If you find
 * yourself writing a `.ant-` selector, the value you want is almost certainly a
 * token here or a component token below.
 *
 * ## colorPrimary is the lime, and that needs a caveat
 *
 * antd spends `colorPrimary` on two different jobs: filled surfaces (primary
 * button, checked checkbox, selected menu item) and thin marks that must read
 * against white (links, active tab ink bar, focus glow). Lime is 2.70:1 on white,
 * so it is right for the first job and wrong for the second.
 *
 * Rather than split the seed — which would make antd's derived palette
 * incoherent — the seed stays lime, matching the rest of the app, and the two
 * places where that fails are corrected directly:
 *
 *   colorLink            -> purple, because a link is text
 *   controlOutlineWidth  -> 0, so antd draws no focus glow of its own and the
 *                           global purple `*:focus-visible` ring from tokens.css
 *                           is the single focus indicator
 *   colorTextLightSolid  -> #1F1F1F, because nothing on a lime fill is white
 *
 * ## controlHeight is 48
 *
 * Per DESIGN_SYSTEM.md §5. Dense property panels want the 40px compact size
 * instead; the mechanism for that is a nested `<ConfigProvider>` around the panel
 * rather than a smaller global default. See the plan's Phase 3.
 */
const antdTheme: ThemeConfig = {
	token: {
		/* Brand */
		colorPrimary: colors.primary,
		colorLink: colors.secondary,
		colorLinkHover: colors.secondaryFillHover,
		/* Kept white: it means "text on a dark solid", and fifteen components read
		   it. The lime surfaces that need a dark label are named individually
		   below — nothing on a lime fill is white (§1), but nothing else is lime. */
		colorTextLightSolid: colors.surface,

		/* Status */
		colorSuccess: colors.primary,
		colorError: colors.danger,
		colorWarning: colors.warningBg,
		colorInfo: colors.secondary,

		/* Surfaces */
		colorBgBase: colors.surface,
		colorBgContainer: colors.surface,
		colorBgElevated: colors.surface,
		colorBgLayout: colors.bgApp,
		colorFillSecondary: colors.fillSubtle,
		colorFillTertiary: colors.fillSubtle,
		colorFillQuaternary: colors.grey50,

		/* Borders */
		colorBorder: colors.border,
		colorBorderSecondary: colors.border,

		/* Text.

		   colorTextBase first: antd derives a dozen greys from it by alpha —
		   colorTextHeading, colorTextLabel, colorIcon, colorSplit, colorBgMask —
		   and its default is pure #000. Overriding colorText alone leaves all of
		   those tinted from black. §1 has no pure black anywhere in the interface,
		   so the ink is the base and every derivation is a tint of it. */
		colorTextBase: colors.text,
		colorText: colors.text,
		colorTextSecondary: colors.textSecondary,
		colorTextTertiary: colors.textSecondary,
		colorTextQuaternary: colors.textDisabled,
		colorTextDescription: colors.textSecondary,
		colorTextPlaceholder: colors.textSecondary,
		colorTextDisabled: colors.textDisabled,

		/* Type */
		fontFamily: font.family,
		fontSize: px(font.sizeBody),
		fontSizeSM: px(font.sizeCaption),
		fontSizeLG: px(font.sizeTitle),
		fontSizeHeading1: px(font.sizeHeading),
		fontSizeHeading2: px(font.sizeHeading),
		fontSizeHeading3: px(font.sizeTitle),
		lineHeight: Number(font.lineHeightBase),
		/* antd's own emphasis weight defaults to 600, which the loaded Roboto set
		   (300/400/500/700) resolves upward to 700. Pin it to the scale's own
		   emphasis weight so antd tables, modal titles and <strong> follow §2. */
		fontWeightStrong: Number(font.weightBold),

		/* Shape */
		borderRadius: px(radius.md),
		borderRadiusSM: px(radius.sm),
		borderRadiusLG: px(radius.lg),
		borderRadiusXS: px(radius.sm),

		/* Sizing */
		controlHeight: px(size.control),
		controlHeightLG: px(size.control),
		controlHeightSM: px(size.controlCompact),

		/* The interface is flat: the only shadow in the system is the modal's. */
		boxShadow: "none",
		boxShadowSecondary: "none",
		boxShadowTertiary: "none",

		/* antd's own focus glow is off; tokens.css draws one purple ring for
		   everything, antd controls included. */
		controlOutlineWidth: 0,

		motionDurationMid: "120ms",
		motionDurationSlow: "120ms",
		wireframe: false,
	},

	components: {
		/* The two antd components that actually put a label on a lime fill.
		   §1: nothing on a lime fill is white. */
		Button: { primaryColor: colors.textOnPrimary, solidTextColor: colors.textOnPrimary },
		Radio: { buttonSolidCheckedColor: colors.textOnPrimary },

		/* Checkbox and Switch go purple, not lime — both show their state with a
		   white glyph or knob, and white on lime is 2.70:1. This is the same
		   carve-out §1 makes for hds/Checkbox and hds/Toggle. */
		Checkbox: { colorPrimary: colors.secondaryFill },
		Switch: { colorPrimary: colors.secondaryFill, colorPrimaryHover: colors.secondaryFillHover },

		/* A tooltip is a white card, not a dark chip — it has to be readable over
		   the 3D viewport as well as over the page. antd draws it from
		   colorBgSpotlight (background) and colorTextLightSolid (text), so both are
		   named here rather than inherited. */
		Tooltip: {
			colorBgSpotlight: colors.surface,
			colorTextLightSolid: colors.text,
			borderRadius: px(radius.md),
			boxShadow: shadow.modal,
		},

		Modal: {
			boxShadow: shadow.modal,
			borderRadiusLG: px(radius.lg),
			padding: px(space[8]),
		},
		/* §4: a menu panel IS a floating layer — it opens over content the page
		   cannot predict, so it takes the modal shadow like a tooltip does. The
		   1px border comes from styles/antd.css, because antd gives these popups
		   no border token; §4 wants both here and only here. Flat panels left
		   these three with no edge at all, which is what this reverses. */
		Dropdown: { boxShadowSecondary: shadow.modal },
		Popover: { boxShadowSecondary: shadow.modal },
		Select: { boxShadowSecondary: shadow.modal, optionSelectedBg: colors.selectedGround, optionSelectedColor: colors.primary },
		/* The underline tab strip — one treatment, set here rather than re-declared
		   per panel. The house pattern is the lime active label the outer
		   FlexLayout strip already uses (Scene.styles.tsx); an ink label with a
		   purple bar, which is what this used to be, left the Viewpoints and
		   Wishlist strips looking like a different control from the Library's.
		   See DESIGN_SYSTEM.md §8 "Underline tab strip" for the contrast note. */
		Tabs: {
			inkBarColor: colors.primary,
			itemSelectedColor: colors.primary,
			itemColor: colors.textSecondary,
			itemHoverColor: colors.primary,
		},
		Notification: { boxShadow: shadow.modal },
		Table: { headerBg: colors.fillSubtle, rowHoverBg: colors.hoverGround },
		/* activeBarBorderWidth draws a 1px rule down the trailing edge of any
		   vertical or inline Menu — an affordance for a sider menu, which this app
		   does not have. Every antd Menu here is the body of a Popover dropdown,
		   where that rule reads as a stray vertical line inside the panel. The
		   panel's own border and shadow come from Popover (§4). */
		Menu: {
			itemSelectedBg: colors.selectedGround,
			itemSelectedColor: colors.primary,
			activeBarBorderWidth: 0,
		},
	},
};

export default antdTheme;
